import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles } from '@emotion/core';
import { Typography, ButtonBase, CircularProgress } from '@material-ui/core';

import PhotoIcon from 'assets/icons/photo';
import HourglassIcon from 'assets/icons/hourglass';
import { ICustomTheme } from 'interfaces';

type Theme = {
  theme?: ICustomTheme;
};

export const StyledCircularProgress = styled(CircularProgress)<{
  theme: ICustomTheme;
}>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
}));

export const alignCenter = css({
  textAlign: 'center',
});

export const cssBorderNone = css({
  border: 'none',
});

export const cssSmall = css({
  height: 235,
  width: 235,
});

export const cssErrorCircle = (theme: ICustomTheme): SerializedStyles =>
  css({
    border: `solid 2px ${theme.colors.error.main}`,
  });

export const cssValidatedCircle = (theme: ICustomTheme): SerializedStyles =>
  css({
    border: `solid 2px ${theme.colors.paletteFromBO.secondary}`,
  });

export const cssGreyCircle = (theme: ICustomTheme): SerializedStyles =>
  css({
    border: `solid 2px ${theme.colors.grey.light}`,
  });

export const CircleIn = styled.div<Theme & { disabled: boolean }>(
  ({ theme, disabled }) => ({
    aspectRatio: '1 / 1',
    position: 'relative',
    width: '100%',
    zIndex: 1,
    '&::before': {
      content: '""',
      backgroundColor: disabled
        ? theme.palette.grey[700]
        : theme.colors.paletteFromBO.primary,
      borderRadius: '50%',
      height: '100%',
      left: 0,
      opacity: '0.1',
      position: 'absolute',
      top: 0,
      width: '100%',
      zIndex: -1,
    },
  }),
);

export const CircleOut = styled.div<Theme>(({ theme }) => ({
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  border: `solid 2px ${theme.colors.paletteFromBO.primary}`,
  padding: '10px',
  width: 320,
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    width: 240,
  },
}));

export const ErrorMessage = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.error.main,
  fontSize: theme.typography.caption.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  marginRight: -600,
  marginTop: theme.spacing(2.5),
  position: 'absolute',
}));

export const Form = styled.form({
  borderRadius: '50%',
  height: '100%',
  textAlign: 'center',
  width: '100%',
});

export const Hourglass = styled(HourglassIcon)<Theme>(({ theme }) => ({
  color: theme.colors.white,
  fontSize: '6rem',
  zIndex: 999,
}));

export const Input = styled.input({
  border: 0,
  clip: 'rect(0, 0, 0, 0)',
  display: 'none',
  height: 1,
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: 1,
});

export const Button = styled((props) => (
  <ButtonBase component="span" {...props} />
))(() => ({
  alignItems: 'center',
  borderRadius: '50%',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'center',
  width: '100%',
}));

type PhotoType = { src: string | null };
export const Photo = styled.div<Theme & PhotoType>(({ theme, src }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.grey[300],
  backgroundImage: `url(${src})`,
  backgroundPosition: 'center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'cover',
  borderRadius: '50%',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  width: '100%',
  '&.overlay::before': {
    background: `linear-gradient(to right, ${theme.colors.white}, ${theme.colors.orange.light})`,
    borderRadius: '50%',
    content: '""',
    height: '100%',
    left: 0,
    opacity: '.7',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
}));

export const CustomPhotoIcon = styled(PhotoIcon)<{
  theme: ICustomTheme;
  disabled: boolean;
}>(({ theme, disabled }) => ({
  fill: disabled ? theme.colors.grey.light : theme.colors.paletteFromBO.primary,
  fontSize: '7rem',
}));

export const PostMyPhoto = styled(Typography)<{
  theme: ICustomTheme;
  disabled: boolean;
}>(({ theme, disabled }) => ({
  color: disabled
    ? theme.colors.grey.light
    : theme.colors.paletteFromBO.primary,
  fontSize: theme.typography.h4.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  maxWidth: 170,
}));

export const RankContainer = styled.span<Theme>(({ theme }) => ({
  position: 'absolute',
  top: 0,
  right: -50,
  [theme.breakpoints.between('md', 'lg')]: {
    right: 0,
  },
  [theme.breakpoints.down('xs')]: {
    right: 0,
  },
}));
